import { useCallback, useMemo, useState } from 'react';
import {
  Table,
  TableCellTypes,
  CellAligns,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import styles from './SummaryStep.module.scss';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const S_NO_COLUMN_WIDTH = 100;
const FILE_NAME_COLUMN_WIDTH = 250;
const ERRORS_COLUMN_WIDTH = 550;

const ERROR_MESSAGE_KEYS = {
  invalidFormat: 'DECLARATIONS_ERROR_INVALID_FORMAT',
  tooLarge: 'DECLARATIONS_ERROR_TOO_LARGE',
  duplicate: 'DECLARATIONS_ERROR_DUPLICATE',
};

const buildColumns = (hasErrors, t) => {
  const columns = [
    {
      id: 'sNo',
      header: t('FIELD_SERIAL_NO'),
      type: TableCellTypes.number,
      align: CellAligns.left,
      sortable: true,
      reorderable: false,
      width: S_NO_COLUMN_WIDTH,
    },
    {
      id: 'fileName',
      header: t('DECLARATIONS_COL_FILE_NAME'),
      type: TableCellTypes.text,
      sortable: true,
      reorderable: false,
      width: FILE_NAME_COLUMN_WIDTH,
    },
  ];

  // Reorderable default (true) is fine — the grip only renders once this column exists.
  if (hasErrors) {
    columns.push({
      id: 'error',
      header: t('DECLARATIONS_COL_ERRORS'),
      type: TableCellTypes.custom,
      width: ERRORS_COLUMN_WIDTH,
      cell: ({ row }) =>
        row.errorCode ? (
          <Alert
            variant={AlertVariants.danger}
            appearance={AlertAppearances.accent}
            orientation={AlertOrientations.horizontal}
            className={styles.errorAlert}
          >
            {t(ERROR_MESSAGE_KEYS[row.errorCode])}
          </Alert>
        ) : null,
    });
  }

  return columns;
};

const compareRows = (a, b, sortId) => {
  if (sortId === 'fileName') return a.fileName.localeCompare(b.fileName);
  return a.sNo - b.sNo;
};

/**
 * Props:
 *   files  the picked files, each with its parse result
 */
export const SummaryStep = ({ files }) => {
  const t = useTranslations();
  const [sort, setSort] = useState(null); // { id, dir } | null
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const rows = useMemo(
    () =>
      files.map((file, index) => ({
        id: file.id,
        sNo: index + 1,
        fileName: file.name,
        errorCode: file.errorCode,
      })),
    [files],
  );

  const hasErrors = useMemo(() => rows.some((row) => row.errorCode), [rows]);
  const columns = useMemo(() => buildColumns(hasErrors, t), [hasErrors, t]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const sign = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => sign * compareRows(a, b, sort.id));
  }, [rows, sort]);

  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize],
  );

  const handleSortChange = useCallback((nextSort) => {
    setSort(nextSort);
    setPage(1);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPage(1);
  }, []);

  const emptyState = useMemo(
    () => ({
      title: t('DECLARATIONS_EMPTY_TITLE'),
      description: t('DECLARATIONS_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  return (
    <div className={styles.wrapper}>
      <Table
        columns={columns}
        data={pageRows}
        total={rows.length}
        defaultPageSize={25}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onSortChange={handleSortChange}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        emptyState={emptyState}
      />
    </div>
  );
};
